'use client';

import { useEffect } from 'react';
import { toast } from 'sonner';
import { downloadStudioProjectZip } from './export';
import { hasUnexportedChanges, markStudioProjectExported, readStudioProject, readStudioProjects } from './project';

const REMIND_EVERY_MS = 20 * 60 * 1000;

/**
 * Studio projects live only in this browser (IndexedDB/localStorage) — the ZIP is the only copy.
 * Warns before leaving the page and every 20 minutes while there are changes not downloaded yet.
 * `projectId` = the project open in the editor; without it every project counts (dashboard).
 */
export function StudioBackupReminder({ projectId }: { projectId?: string }) {
  useEffect(() => {
    const unexported = () =>
      projectId ? hasUnexportedChanges(readStudioProject(projectId)) : readStudioProjects().some(hasUnexportedChanges);

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!unexported()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    const timer = window.setInterval(() => {
      if (!unexported()) return;
      const project = projectId ? readStudioProject(projectId) : null;
      toast.warning('Masz zmiany niepobrane do ZIP. Projekty Studio są zapisane tylko w tej przeglądarce.', {
        duration: 15000,
        action: project ? {
          label: 'Pobierz ZIP',
          onClick: () => {
            void downloadStudioProjectZip(project).then(() => markStudioProjectExported(project.id));
          },
        } : undefined,
      });
    }, REMIND_EVERY_MS);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.clearInterval(timer);
    };
  }, [projectId]);

  return null;
}
